if (!window.Eurus.loadedScript.has('judgeme-reviews-page.js')) {
  window.Eurus.loadedScript.add('judgeme-reviews-page.js');

  // No requestAnimationFrame wrapper: rAF never fires in a backgrounded tab, which
  // would silently skip registering the component.
  {
    document.addEventListener('alpine:init', () => {
      Alpine.data('xJudgemeReviewsPage', (config) => ({
        shopDomain: config.shopDomain,
        apiToken: config.apiToken,
        all: [],          // everything fetched from Judge.me
        reviews: [],      // what is currently on screen
        rating: null,
        total: null,
        state: 'loading', // loading | ready | empty | error
        batch: 6,
        loadingMore: false,
        observer: null,
        onScroll: null,
        scrollQueued: false,

        get hasMore() {
          return this.reviews.length < this.all.length;
        },

        init() {
          this.load().then(() => {
            this.watchSentinel();
            this.$nextTick(() => this.fillViewport());
          });
        },

        destroy() {
          if (this.observer) this.observer.disconnect();
          if (this.onScroll) {
            window.removeEventListener('scroll', this.onScroll);
            window.removeEventListener('resize', this.onScroll);
          }
        },

        // Reveal the next batch when the sentinel scrolls into view. The delay is
        // deliberate: without it the spinner flashes for a frame and reads as a
        // glitch rather than as loading.
        watchSentinel() {
          const el = this.$refs.sentinel;
          if (!el) return;

          if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver((entries) => {
              entries.forEach((e) => {
                if (e.isIntersecting) this.more();
              });
            }, { rootMargin: '300px 0px' });
            this.observer.observe(el);
          }

          // A scroll listener as well, and not belt-and-braces: an
          // IntersectionObserver only fires when intersection CHANGES. Once the
          // sentinel is on screen it stays "intersecting", so no further callback
          // arrives and loading stalls after a single batch — exactly what happened
          // here (stuck at 12 of 15). Scroll always fires, so it always recovers.
          // Throttled with a timer, not requestAnimationFrame: rAF is throttled to
          // a stop in background tabs, which would leave the "queued" flag stuck
          // true and kill every later scroll.
          this.onScroll = () => {
            if (this.scrollQueued) return;
            this.scrollQueued = true;
            setTimeout(() => {
              this.scrollQueued = false;
              this.fillViewport();
            }, 120);
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
        },

        more() {
          if (this.loadingMore || !this.hasMore) return;
          this.loadingMore = true;
          setTimeout(() => {
            this.reviews = this.all.slice(0, this.reviews.length + this.batch);
            this.loadingMore = false;
            // IntersectionObserver only fires when intersection CHANGES. If the
            // sentinel is still on screen after this batch (short page, tall
            // viewport), no further callback ever comes and loading stalls — so
            // check again ourselves and keep going until it is pushed out of view.
            this.$nextTick(() => this.fillViewport());
          }, 350);
        },

        fillViewport() {
          const el = this.$refs.sentinel;
          if (!el || !this.hasMore || this.loadingMore) return;
          const r = el.getBoundingClientRect();
          if (r.top < window.innerHeight + 300) this.more();
        },

        endpoint(name) {
          return (
            'https://api.judge.me/api/v1/widgets/' +
            name +
            '?shop_domain=' +
            encodeURIComponent(this.shopDomain) +
            '&api_token=' +
            encodeURIComponent(this.apiToken)
          );
        },

        async load() {
          try {
            // The public token reaches the widget endpoints only. `featured_carousel`
            // is the one that returns real review CONTENT — `all_reviews_page` comes
            // back with an empty body on this shop, and `/api/v1/reviews` refuses a
            // public token outright ("not enough permissions").
            const [carouselRes, ratingRes, countRes] = await Promise.all([
              fetch(this.endpoint('featured_carousel')).then((r) => r.json()),
              fetch(this.endpoint('all_reviews_rating')).then((r) => r.json()).catch(() => null),
              fetch(this.endpoint('all_reviews_count')).then((r) => r.json()).catch(() => null)
            ]);

            if (ratingRes && ratingRes.all_reviews_rating) {
              this.rating = parseFloat(ratingRes.all_reviews_rating);
            }
            if (countRes && countRes.all_reviews_count) {
              this.total = parseInt(countRes.all_reviews_count, 10);
            }

            const html = carouselRes && carouselRes.featured_carousel;
            if (!html) {
              this.state = 'empty';
              return;
            }

            this.all = this.parse(html);
            this.reviews = this.all.slice(0, this.batch);
            this.state = this.all.length ? 'ready' : 'empty';
          } catch (e) {
            console.error('[judgeme-reviews-page] load failed', e);
            this.state = 'error';
          }
        },

        // Judge.me returns rendered markup, not JSON. Parse it in a detached
        // document so their styles and scripts never touch this page — we only
        // want the data, and render it in the Rosalia design ourselves.
        parse(html) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const text = (el) => (el ? el.textContent.trim() : '');

          return Array.from(doc.querySelectorAll('.jdgm-carousel-item')).map((item) => {
            const ratingEl = item.querySelector('.jdgm-carousel-item__review-rating');
            return {
              name: text(item.querySelector('.jdgm-carousel-item__reviewer-name')),
              date: text(item.querySelector('.jdgm-carousel-item__timestamp')),
              title: text(item.querySelector('.jdgm-carousel-item__review-title')),
              body: text(item.querySelector('.jdgm-carousel-item__review-content')),
              stars: ratingEl ? ratingEl.querySelectorAll('.jdgm-star.jdgm--on').length : 0
            };
          }).filter((r) => r.body || r.title);
        },

        starsFor(n) {
          return [1, 2, 3, 4, 5].map((i) => i <= n);
        },

        initials(name) {
          return (name || '?')
            .split(/\s+/)
            .slice(0, 2)
            .map((w) => w.charAt(0).toUpperCase())
            .join('');
        }
      }));
    });
  }
}
